import React, { useState, useEffect, useContext } from "react";
import productStyles from "./Product.module.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Link, useLocation, useParams } from "react-router-dom";
import Image from "react-bootstrap/Image";
import Alert from "react-bootstrap/Alert";
import { EmptyCartContext } from "../../context/EmptyCartContext";
import { ProductDetailContext } from "../../context/ProductDetailContext";
import { CartContext } from "../../context/CartContext";
import { ProductSpecsContext } from "../../context/ProductSpecsContext";
import { ProductContext } from "../../context/ProductContext";
import Spinner from "react-bootstrap/Spinner";
import axios from "axios";

const Product = () => {
  const location = useLocation();
  const { id } = useParams();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  let currentPath = "";
  const crumbs = pathSegments.map((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;

    return (
      <span key={currentPath}>
        {isLast ? (
          <span>{segment.charAt(0).toUpperCase() + segment.slice(1)}</span>
        ) : (
          <>
            <Link to={currentPath} className="text-decoration-none text-muted">
              {segment.charAt(0).toUpperCase() + segment.slice(1)}
            </Link>
            <span className="mx-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.53073 2.46937L11.5307 7.46937C11.6007 7.53905 11.6561 7.62184 11.694 7.71301C11.7318 7.80417 11.7513 7.90191 11.7513 8.00062C11.7513 8.09933 11.7318 8.19707 11.694 8.28824C11.6561 8.3794 11.6007 8.46219 11.5307 8.53187L6.53073 13.5319C6.38984 13.6728 6.19874 13.7519 5.99948 13.7519C5.80023 13.7519 5.60913 13.6728 5.46823 13.5319C5.32734 13.391 5.24818 13.1999 5.24818 13.0006C5.24818 12.8014 5.32734 12.6103 5.46823 12.4694L9.93761 8L5.46761 3.53062C5.32671 3.38973 5.24756 3.19863 5.24756 2.99937C5.24756 2.80011 5.32671 2.60902 5.46761 2.46812C5.60851 2.32723 5.7996 2.24807 5.99886 2.24807C6.19812 2.24807 6.38921 2.32723 6.53011 2.46812L6.53073 2.46937Z"
                  fill="black"
                  fillOpacity="0.6"
                />
              </svg>
            </span>
          </>
        )}
      </span>
    );
  });

  const [result, setResult] = useState({});
  const [loading, setLoading] = useState(true);

  const { productID, setProductID } = useContext(ProductContext);
  const { productDetail, setProductDetail } = useContext(ProductDetailContext);
  const { addToCart, cart } = useContext(CartContext);
  const { emptyCart, setEmptyCart } = useContext(EmptyCartContext);
  const [sizeVariations, setSizeVariations] = useState([]);
  const { productSpecs, setProductSpecs } = useContext(ProductSpecsContext);

  const currentProductID = id || productID || localStorage.getItem("productID");

  const [productImages, setProductImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [sizebtn, setSizeBtn] = useState(0);
  const [productQty, setProductQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState(sizeVariations[0]);
  
  // ✅ Stock validation states
  const [stockError, setStockError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [availableStock, setAvailableStock] = useState(0);

  const processProductImages = (productData) => {
    const images = [];
    const fallbackImage =
      "https://images.pexels.com/photos/264726/pexels-photo-264726.jpeg";

    if (productData.images && Array.isArray(productData.images)) {
      images.push(
        ...productData.images.map((img, index) => ({
          id: index + 1,
          image:
            typeof img === "string"
              ? img
              : img.url || img.image_link || img.src || fallbackImage,
          alt:
            typeof img === "object" && img.alt
              ? img.alt
              : `Product image ${index + 1}`,
        }))
      );
    } else if (
      productData.image_links &&
      Array.isArray(productData.image_links)
    ) {
      images.push(
        ...productData.image_links.map((img, index) => ({
          id: index + 1,
          image: img,
          alt: `Product image ${index + 1}`,
        }))
      );
    } else if (productData.image_link) {
      if (Array.isArray(productData.image_link)) {
        images.push(
          ...productData.image_link.map((img, index) => ({
            id: index + 1,
            image: img,
            alt: `Product image ${index + 1}`,
          }))
        );
      } else {
        images.push({
          id: 1,
          image: productData.image_link,
          alt: "Product image",
        });
      }
    }

    const validImages = images.filter(
      (img) =>
        img.image &&
        typeof img.image === "string" &&
        img.image.trim() !== "" &&
        img.image !== "undefined" &&
        img.image !== "null"
    );

    if (validImages.length === 0) {
      validImages.push({
        id: 1,
        image: fallbackImage,
        alt: "Product image",
      });
    }

    setProductImages(validImages);
    setSelectedImageIndex(0);
  };

  const getCurrentImage = () => {
    if (productImages.length > 0 && productImages[selectedImageIndex]) {
      const currentImg = productImages[selectedImageIndex].image;
      if (currentImg && typeof currentImg === "string" && currentImg.trim()) {
        return currentImg;
      }
    }

    if (result.image_link) {
      if (Array.isArray(result.image_link) && result.image_link.length > 0) {
        return result.image_link[0];
      } else if (typeof result.image_link === "string") {
        return result.image_link;
      }
    }

    return "https://via.placeholder.com/400x400?text=No+Image";
  };

  const createCartProduct = () => {
    const getCartImage = () => {
      const currentImg = getCurrentImage();
      if (
        currentImg &&
        currentImg !== "https://via.placeholder.com/400x400?text=No+Image"
      ) {
        return currentImg;
      }

      if (result.image_link) {
        if (Array.isArray(result.image_link) && result.image_link.length > 0) {
          return result.image_link[0];
        } else if (typeof result.image_link === "string") {
          return result.image_link;
        }
      }

      return "https://via.placeholder.com/300x300?text=Product";
    };

    return {
      _id: result._id,
      title: result.Title || result.title || "Unknown Product",
      Title: result.Title || result.title || "Unknown Product",
      price: result.price || 0,
      discountedPrice: result.price || 0,
      oldPrice: result.oldPrice || (result.price ? result.price * 1.2 : 0),
      image: getCartImage(),
      image_link: result.image_link,
      quantity: parseInt(productQty) || 1,
      selectedSize: selectedSize,
      description: result.Description || result.description || "",
      rating: result.rating || 4.5,
    };
  };

  // ✅ Calculate available stock considering cart items
  const calculateAvailableStock = () => {
    const productStock = result.quantity || 0;
    
    // Find quantity already in cart for this product with same size
    const cartItem = cart?.find(item => 
      item._id === result._id && 
      item.selectedSize === selectedSize
    );
    const quantityInCart = cartItem ? cartItem.quantity : 0;
    
    // Available stock = total stock - quantity already in cart
    const available = productStock - quantityInCart;
    return available > 0 ? available : 0;
  };

  // ✅ Validate stock before quantity change
  const handleQuantityIncrease = () => {
    const available = availableStock;
    
    if (productQty >= available) {
      setStockError(`Only ${available} items available in stock`);
      setTimeout(() => setStockError(""), 3000);
      return;
    }
    
    setStockError("");
    setProductQty(productQty + 1);
  };

  const handleQuantityDecrease = () => {
    if (productQty > 1) {
      setStockError("");
      setProductQty(productQty - 1);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      if (currentProductID) {
        if (id && id !== productID) {
          setProductID(id);
          localStorage.setItem("productID", id);
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE}product/getProductById?id=${currentProductID}`
        );
        
        if (response.data && response.data.prod) {
          const productData = response.data.prod;
          setResult(productData);
          setProductDetail(productData);
          processProductImages(productData);
        }
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentProductID) {
      fetchData();
    }
  }, [id, currentProductID]);

  // ✅ Initialize available stock when product is loaded
  useEffect(() => {
    if (result._id && result.quantity !== undefined) {
      const available = calculateAvailableStock();
      setAvailableStock(available);
    }
  }, [result._id, result.quantity]);

  useEffect(() => {
    if (productDetail && productDetail.size) {
      setSizeVariations(productDetail.size);
      setSelectedSize(productDetail.size[0]);
    }
  }, [productDetail]);

  // ✅ Recalculate available stock when cart changes, result changes, or size changes
  useEffect(() => {
    if (result._id) {
      const available = calculateAvailableStock();
      setAvailableStock(available);
      // Reset quantity when switching sizes
      setProductQty(1);
      setStockError("");
    }
  }, [cart, result._id, selectedSize]);

  // ✅ Add to cart with stock validation
  const handleAddToCart = () => {
    const available = availableStock;
    
    // Check if product is out of stock
    if (available <= 0) {
      setStockError("This product is out of stock");
      setTimeout(() => setStockError(""), 3000);
      return;
    }
    
    // Check if trying to add more than available
    if (productQty > available) {
      setStockError(`Only ${available} items available in stock`);
      setTimeout(() => setStockError(""), 3000);
      return;
    }

    const cartProduct = createCartProduct();
    setEmptyCart(false);
    addToCart(cartProduct);
    setStockError("");
    setSuccessMessage("Product added to cart successfully!");
    
    // Reset quantity after successful add
    setProductQty(1);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
  };

  // ✅ Check if product is out of stock
  const isOutOfStock = () => {
    return result.quantity === 0 || availableStock <= 0;
  };

  return (
    <section className={productStyles.productcontainer}>
      <Container fluid className="py-lg-5 py-3">
        <Row>
          <Col lg={12} className="border-top border-tertiary py-lg-4 pt-4">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb" style={{ fontSize: "14px" }}>
                <li className="breadcrumb-item me-2">
                  <Link to="/" className="text-decoration-none text-muted">
                    Home
                  </Link>
                </li>
                <span className="me-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.53073 2.46937L11.5307 7.46937C11.6007 7.53905 11.6561 7.62184 11.694 7.71301C11.7318 7.80417 11.7513 7.90191 11.7513 8.00062C11.7513 8.09933 11.7318 8.19707 11.694 8.28824C11.6561 8.3794 11.6007 8.46219 11.5307 8.53187L6.53073 13.5319C6.38984 13.6728 6.19874 13.7519 5.99948 13.7519C5.80023 13.7519 5.60913 13.6728 5.46823 13.5319C5.32734 13.391 5.24818 13.1999 5.24818 13.0006C5.24818 12.8014 5.32734 12.6103 5.46823 12.4694L9.93761 8L5.46761 3.53062C5.32671 3.38973 5.24756 3.19863 5.24756 2.99937C5.24756 2.80011 5.32671 2.60902 5.46761 2.46812C5.60851 2.32723 5.7996 2.24807 5.99886 2.24807C6.19812 2.24807 6.38921 2.32723 6.53011 2.46812L6.53073 2.46937Z"
                      fill="black"
                      fillOpacity="0.6"
                    />
                  </svg>
                </span>
                {crumbs.length > 0 && crumbs}
              </ol>
            </nav>
          </Col>

          {loading && (
            <Col lg={12}>
              <div className="text-center py-5">
                <Spinner animation="grow" />
                <p className="mt-3">Loading product details...</p>
              </div>
            </Col>
          )}

          {!loading && !currentProductID && (
            <Col lg={12}>
              <div className="text-center py-5">
                <p>No product ID provided.</p>
                <Link to="/shop" className="btn btn-primary">
                  Back to Shop
                </Link>
              </div>
            </Col>
          )}

          {!loading && currentProductID && Object.keys(result).length === 0 && (
            <Col lg={12}>
              <div className="text-center py-5">
                <p>Product not found.</p>
                <Link to="/shop" className="btn btn-primary">
                  Back to Shop
                </Link>
              </div>
            </Col>
          )}

          {!loading && Object.keys(result).length > 0 && (
            <>
              <Col lg={6} md={6}>
                <div className="d-flex flex-column">
                  <div className="w-100 mb-3">
                    <Image
                      src={getCurrentImage()}
                      alt={
                        productImages[selectedImageIndex]?.alt ||
                        "Product image"
                      }
                      fluid
                      className={`${productStyles.productimg} rounded-4`}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/600x600?text=No+Image";
                      }}
                    />

                    {productImages.length > 1 && (
                      <div className="text-center mt-2">
                        <small className="text-muted">
                          {selectedImageIndex + 1} of {productImages.length}
                        </small>
                      </div>
                    )}
                  </div>

                  {productImages.length > 1 && (
                    <div
                      className="d-flex"
                      style={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        gap: "8px",
                        paddingBottom: "8px",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#ccc transparent",
                      }}
                    >
                      {productImages.map((item, index) => (
                        <div
                          key={item.id}
                          onClick={() => handleThumbnailClick(index)}
                          style={{
                            cursor: "pointer",
                            minWidth: "80px",
                            maxWidth: "80px",
                            height: "80px",
                            flexShrink: 0,
                          }}
                        >
                          <Image
                            src={item.image}
                            alt={item.alt}
                            className={`${
                              selectedImageIndex === index
                                ? productStyles.selectedthumb
                                : ""
                            } rounded-3`}
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "contain",
                              border:
                                selectedImageIndex === index
                                  ? "2px solid #0d6efd"
                                  : "2px solid transparent",
                              transition: "border-color 0.2s ease",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/80x80?text=No+Image";
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              <Col lg={6} md={6} className="ps-lg-4">
                <div>
                  <h2
                    className={`${productStyles.producttitle} text-uppercase`}
                  >
                    {result.Title || result.title || "Product Title"}
                  </h2>
                </div>

                {/* ✅ Stock indicator */}
                <div className="mb-2">
                  {isOutOfStock() ? (
                    <span className="badge bg-danger">Out of Stock</span>
                  ) : availableStock <= 5 ? (
                    <span className="badge bg-warning text-dark">
                      Only {availableStock} left in stock
                    </span>
                  ) : (
                    <span className="badge bg-success">In Stock</span>
                  )}
                </div>

                <div className="d-flex align-items-center pb-2">
                  <div className="pt-2">
                    <h2
                      className={`${productStyles.sliderprice} me-2 fw-semibold`}
                    >
                      ${result.price?.toFixed(2) || "0.00"}
                    </h2>
                  </div>
                  {result.oldPrice && (
                    <>
                      <div className="pt-2">
                        <h2
                          className={`${productStyles.slideroldprice} me-2 fw-semibold`}
                        >
                          ${result.oldPrice}
                        </h2>
                      </div>
                      <div className={productStyles.sliderdiscountbg}>
                        <span className={productStyles.sliderdiscounttext}>
                          {100 -
                            Math.round((result.price / result.oldPrice) * 100)}
                          %
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="border-bottom border-tertiary">
                  <p className={productStyles.productdesctext}>
                    {result.Description ||
                      result.description ||
                      "No description available"}
                  </p>
                </div>

                {sizeVariations.length > 0 && (
                  <div className="py-4 border-bottom border-tertiary">
                    <p className={productStyles.productdesctext}>Choose Size</p>
                    <div className={productStyles.sizebtncontainer}>
                      {sizeVariations.map((item, index) => (
                        <div key={index}>
                          <button
                            className={
                              sizebtn === index
                                ? productStyles.activesizebtn
                                : productStyles.sizebtn
                            }
                            onClick={() => {
                              setSizeBtn(index);
                              setSelectedSize(item);
                            }}
                            disabled={isOutOfStock()}
                          >
                            {item}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ✅ Success message */}
                {successMessage && (
                  <Alert variant="success" className="mt-3" dismissible onClose={() => setSuccessMessage("")}>
                    {successMessage}
                  </Alert>
                )}

                {/* ✅ Stock error alert */}
                {stockError && (
                  <Alert variant="danger" className="mt-3" dismissible onClose={() => setStockError("")}>
                    {stockError}
                  </Alert>
                )}

                <div className="py-4 d-flex">
                  <div className="me-3">
                    <button 
                      className={productStyles.qtybtn}
                      disabled={isOutOfStock()}
                    >
                      <svg
                        onClick={handleQuantityDecrease}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ 
                          cursor: productQty > 1 && !isOutOfStock() ? "pointer" : "not-allowed",
                          opacity: productQty > 1 && !isOutOfStock() ? 1 : 0.5
                        }}
                      >
                        <path
                          d="M21.375 12C21.375 12.2984 21.2565 12.5845 21.0455 12.7955C20.8345 13.0065 20.5484 13.125 20.25 13.125H3.75C3.45163 13.125 3.16548 13.0065 2.9545 12.7955C2.74353 12.5845 2.625 12.2984 2.625 12C2.625 11.7016 2.74353 11.4155 2.9545 11.2045C3.16548 10.9935 3.45163 10.875 3.75 10.875H20.25C20.5484 10.875 20.8345 10.9935 21.0455 11.2045C21.2565 11.4155 21.375 11.7016 21.375 12Z"
                          fill="black"
                        />
                      </svg>
                      <span>{productQty}</span>
                      <svg
                        onClick={handleQuantityIncrease}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ 
                          cursor: !isOutOfStock() && productQty < availableStock ? "pointer" : "not-allowed",
                          opacity: !isOutOfStock() && productQty < availableStock ? 1 : 0.5
                        }}
                      >
                        <path
                          d="M21.375 12C21.375 12.2984 21.2565 12.5845 21.0455 12.7955C20.8345 13.0065 20.5484 13.125 20.25 13.125H13.125V20.25C13.125 20.5484 13.0065 20.8345 12.7955 21.0455C12.5845 21.2565 12.2984 21.375 12 21.375C11.7016 21.375 11.4155 21.2565 11.2045 21.0455C10.9935 20.8345 10.875 20.5484 10.875 20.25V13.125H3.75C3.45163 13.125 3.16548 13.0065 2.9545 12.7955C2.74353 12.5845 2.625 12.2984 2.625 12C2.625 11.7016 2.74353 11.4155 2.9545 11.2045C3.16548 10.9935 3.45163 10.875 3.75 10.875H10.875V3.75C10.875 3.45163 10.9935 3.16548 11.2045 2.9545C11.4155 2.74353 11.7016 2.625 12 2.625C12.2984 2.625 12.5845 2.74353 12.7955 2.9545C13.0065 3.16548 13.125 3.45163 13.125 3.75V10.875H20.25C20.5484 10.875 20.8345 10.9935 21.0455 11.2045C21.2565 11.4155 21.375 11.7016 21.375 12Z"
                          fill="black"
                        />
                      </svg>
                    </button>
                  </div>
                  <div>
                    <button
                      className={productStyles.addtocartbtn}
                      onClick={handleAddToCart}
                      disabled={isOutOfStock()}
                      style={{
                        opacity: isOutOfStock() ? 0.6 : 1,
                        cursor: isOutOfStock() ? "not-allowed" : "pointer"
                      }}
                    >
                      {isOutOfStock() ? "Out of Stock" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              </Col>
            </>
          )}
        </Row>
      </Container>
    </section>
  );
};

export default Product;